//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

// functions
import { useTranslation } from 'react-i18next';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { store } from '../app/store';

import { SettingsState } from '../app/settings-slice';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C O N S T A N T S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  F U N C T I O N S
//
//  --------------------------------------------------------------------------

//  ------------------------------------------------------------
//
//  Handle the user clicking on an item in the browser.
//
//  ------------------------------------------------------------

export function brw_handleItemClick(args: {
  divID: number;
  shiftKey: boolean;
  ctrlKey: boolean;
  doubleClick?: boolean;
  selectedDivs: number[];
  setSelectedDivs: any;
  addToSelectedDivs: any;
  removeFromSelectedDivs: any;
  handleDoubleClick?: any;
  focusElement: number;
  setFocusElement: any;
}) {
  //
  const stateFetch: any = store.getState();

  let alreadyHandled: boolean = false;
  const alreadySelected: boolean = args.selectedDivs.indexOf(args.divID) > -1;

  // handle a double click.
  if (
    args.doubleClick === true &&
    args.handleDoubleClick !== undefined &&
    !alreadyHandled
  ) {
    args.handleDoubleClick(args.divID);
    alreadyHandled = true;
  }

  // handle a click without shift or ctrl.
  if (
    args.divID > -1 &&
    !alreadyHandled &&
    ((args.ctrlKey === false && args.shiftKey === false && alreadySelected === false) ||
      args.selectedDivs.length === 0)
  ) {
    store.dispatch(args.setSelectedDivs([args.divID]));
    alreadyHandled = true;
  }

  // handle a click with ctrl.
  if (!alreadyHandled && args.ctrlKey && !args.shiftKey) {
    store.dispatch(args.addToSelectedDivs([args.divID]));
    alreadyHandled = true;
  }

  // handle a click with shift.
  if (!alreadyHandled && args.shiftKey && args.selectedDivs.length > 0) {
    //
    _handleSelectedRangeUpdate({
      fromID: args.focusElement,
      toID: args.divID,
      selectedDivs: args.selectedDivs,
      addToSelectedDivs: args.addToSelectedDivs,
      removeFromSelectedDivs: args.removeFromSelectedDivs,
    });
    //
  }

  // set the focus.
  store.dispatch(args.setFocusElement({ divID: args.divID }));
  //
} // brw_handleItemClick

//  ------------------------------------------------------------
//
//  handle all key presses on an item browser.
//
//  ------------------------------------------------------------

export function brw_keyDownHandler(args: {
  event: React.KeyboardEvent<HTMLElement>;
  dataItems: number;
  itemsPerRow: number;
  selectedDivs: number[];
  setSelectedDivs?: any;
  addToSelectedDivs?: any;
  removeFromSelectedDivs?: any;
  focusElement: number;
  setFocusElement: any;
}) {
  //
  const stateFetch: any = store.getState();

  // if anything other than the arrow keys is pressed then go no further.
  const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
  if (!arrowKeys.includes(args.event.key)) {
    return false;
  }

  // if there are no data items then go no further.
  if (args.dataItems === 0) {
    return false;
  }

  // get the currently focused item, and work out the next item
  // to take the focus.
  const oldFocusID = args.focusElement;
  let newFocusID = oldFocusID;

  switch (args.event.key) {
    case 'ArrowLeft':
      if (oldFocusID > 0) {
        newFocusID = oldFocusID - 1;
      }
      break;
    case 'ArrowRight':
      if (oldFocusID < args.dataItems - 1) {
        newFocusID = oldFocusID + 1;
      }
      break;
    case 'ArrowUp':
      if (oldFocusID >= args.itemsPerRow) {
        newFocusID = oldFocusID - args.itemsPerRow;
      }
      break;
    case 'ArrowDown':
      if (oldFocusID < args.dataItems - args.itemsPerRow) {
        newFocusID = oldFocusID + args.itemsPerRow;
      }
      break;
  }

  // if shift is pressed, then make a list of the div elements between the old
  // and new focus, and swap the selection status. the only exception is the
  // last selected element before we reach the new element, which should stay
  // selected.
  if (
    args.event.shiftKey === true &&
    args.setSelectedDivs !== undefined &&
    args.addToSelectedDivs !== undefined &&
    args.removeFromSelectedDivs !== undefined
  ) {
    //
    _handleSelectedRangeUpdate({
      fromID: oldFocusID,
      toID: newFocusID,
      selectedDivs: args.selectedDivs,
      addToSelectedDivs: args.addToSelectedDivs,
      removeFromSelectedDivs: args.removeFromSelectedDivs,
    });
    //
  }

  // if shift is not pressed, then remove all selected items.
  if (args.event.shiftKey === false && args.setSelectedDivs !== undefined) {
    store.dispatch(args.setSelectedDivs([]));
  }

  // move focus to the next index.
  store.dispatch(args.setFocusElement({ divID: newFocusID }));

  // return handled flag.
  return true;
  //
} // brw_keyDownHandler

//  ------------------------------------------------------------
//
//  if shift is pressed, and a new item selected, then
//  handle the change in the selected range.
//
//  ------------------------------------------------------------

function _handleSelectedRangeUpdate(args: {
  fromID: number;
  toID: number;
  selectedDivs: number[];
  addToSelectedDivs: any;
  removeFromSelectedDivs: any;
}) {
  //
  // make a list of all div element ids in the range, and find the selection
  // status.
  let divs: number[] = Array(Math.abs(args.toID - args.fromID) + 1)
    .fill(0)
    .map((_, idx) => Math.min(args.toID, args.fromID) + idx);
  let selected: boolean[] = divs.map((map) => {
    return args.selectedDivs.indexOf(map) > -1;
  });

  // there will be one element we don't want to swap. this is the original focus in the selected items.
  const lastSelectedIndex: number =
    args.toID > args.fromID ? selected.lastIndexOf(true) : selected.indexOf(true);
  divs = divs.filter((filter, idx) => idx !== lastSelectedIndex);
  selected = selected.filter((filter, idx) => idx !== lastSelectedIndex);

  // add elements to the selection if they are not currently selected.
  store.dispatch(
    args.addToSelectedDivs(divs.filter((filter, idx) => selected[idx] === false)),
  );

  // remove elements from the selection if they are currently selected.
  store.dispatch(
    args.removeFromSelectedDivs(divs.filter((filter, idx) => selected[idx] === true)),
  );
  //
} // _handleSelectedRangeUpdate
